import DATimeline from './DATimeline';
import { daysSinceStatusRecorded, formatDADate } from '../lib/daTimeline';
import { useState, useEffect, useMemo, useRef, useLayoutEffect } from 'react';
import { COLORS, STATUS_COLORS, CARD_SHADOW } from '../styles/design-tokens';
import { useAuth } from '../context/AuthContext';
import { useSubscription } from '../context/SubscriptionContext';
import { supabase } from '../supabaseClient';
import { showToast } from '../lib/toast';
import ProIntelligence, { hasProIntelligenceContent, isProSnapshotMissing } from './ProIntelligence';
import DALifecycleSection from './DALifecycleSection';
import ProBadge from './ui/ProBadge';
import StatusBadge from './ui/StatusBadge';
import CategoryPill from './ui/CategoryPill';
import AppTypeBadge from './ui/AppTypeBadge';
import { isPlaceholderAddress, addressLocality, unavailableAddressLabel } from '../lib/addressQuality';
import { hasValidCoords } from '../lib/utils';
import { fetchDAProDetail, proDataCache } from '../lib/fetchDAProDetail';
import { useDANote } from '../hooks/useDANote';
import { GMAP_KEY } from '../lib/constants';
import { getCouncilTrackerUrl, councilLinkLabel } from '../lib/councilLinks';
import CouncilSearchHelp from './ui/CouncilSearchHelp';
import { trackDAEvent } from '../lib/analytics';
import { canAccessProCouncil } from '../lib/proAccess';
import { logProIntelligenceGap, logSlowProFetch } from '../lib/proGapEvents';
import { refreshAppShell } from '../lib/appRecovery';
import { reportClientDiagnostic } from '../lib/clientDiagnostics';
import { displayDAAddress, displayDAIdentifier } from '../lib/daDisplay';
import { getPipelineCost } from '../lib/pipelineCost';
import { getDwellings, getStoreys } from '../lib/daMeasurements';
import { isSupabaseCircuitError } from '../lib/supabaseCircuitBreaker';
import { getCategoryLabel, resolveProposalType } from '../lib/daCategories';
import { useIsMobile } from '../hooks/useIsMobile';

function formatCost(cost) {
  if (!cost || cost === 0) return null;
  if (cost >= 1000000) return `$${(cost / 1000000).toFixed(1)}M`;
  if (cost >= 1000) return `$${(cost / 1000).toFixed(0)}K`;
  return `$${cost.toLocaleString()}`;
}

function truncateEvidenceQuote(value, max = 150) {
  const text = String(value || '').replace(/\s+/g, ' ').trim();
  if (text.length <= max) return text;
  return `${text.slice(0, max - 1).trimEnd()}…`;
}

function proRetryDelayMs(error) {
  if (isSupabaseCircuitError(error)) {
    const retryAfterMs = Number(error?.retryAfterMs || 0);
    if (Number.isFinite(retryAfterMs) && retryAfterMs > 0) {
      return Math.min(30_000, Math.max(900, retryAfterMs + 250));
    }
  }
  return 900;
}

const PRO_AUTO_RETRY_LIMIT = 2;
const PRO_RETRYING_MESSAGE = 'Pro Intelligence is taking longer than expected. Retrying...';

function StatTile({ label, value, color }) {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div style={{
      flex: 1, background: COLORS.card, border: `1px solid ${COLORS.cardBorder}`,
      borderRadius: 8, padding: "10px 8px", textAlign: "center",
    }}>
      <div style={{ color: COLORS.textDim, fontSize: 10, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 3 }}>{label}</div>
      <div style={{ color: color || COLORS.text, fontSize: 15, fontWeight: 700, lineHeight: 1.2 }}>{value}</div>
    </div>
  );
}

export default function DADetailView({ app, onBack, onTrack, isTr, onShowOnMap, tracked, theme, isDesktopLayout, initialScrollTop = 0, onScrollPositionChange }) {
  const { user, loading: authLoading } = useAuth();
  const { isPro, tier, proCouncils, loading: subLoading } = useSubscription();
  const detailRef = useRef(null);
  useLayoutEffect(() => {
    if (detailRef.current) detailRef.current.scrollTop = initialScrollTop;
  }, [app.id, initialScrollTop]);
  const [proData, setProData] = useState(null);
  const [proLoading, setProLoading] = useState(false);
  const [proError, setProError] = useState(null);
  const [proRetryState, setProRetryState] = useState({ scope: '', count: 0 });
  const [sharePulsed, setSharePulsed] = useState(false);
  const [mapPreviewFailed, setMapPreviewFailed] = useState(false);
  const isNarrowPhone = useIsMobile(700);
  const isDesktop = typeof isDesktopLayout === 'boolean' ? isDesktopLayout : !isNarrowPhone;
  const note = useDANote(user?.id, app.id, true);
  const lat = Number(app.latitude);
  const lng = Number(app.longitude);
  const hasCoords = hasValidCoords(app);
  const appId = app.id;
  const appCouncil = app.council;
  const appDaNumber = app.da_number;
  const appSource = app.source;
  const appIsPrimary = app.is_primary;
  const appParentDaId = app.parent_da_id;
  const userId = user?.id;
  const proRetryScope = `${userId || 'anonymous'}:${appId}`;
  const proRetry = proRetryState.scope === proRetryScope ? proRetryState.count : 0;
  const diagnosticUser = useMemo(() => userId ? { id: userId } : null, [userId]);
  const proDiagnosticDa = useMemo(() => ({
    id: appId,
    council: appCouncil,
    da_number: appDaNumber,
    source: appSource,
    is_primary: appIsPrimary,
    parent_da_id: appParentDaId,
  }), [appId, appCouncil, appDaNumber, appSource, appIsPrimary, appParentDaId]);

  // Derive display values
  const cost = formatCost(getPipelineCost(app));
  const dwellings = getDwellings(app);
  const storeys = getStoreys(app);
  const lodgedDate = formatDADate(app.lodged_date);
  const displayStatus = app.status;
  const displayIdentifier = displayDAIdentifier(app);
  const daysInStatus = daysSinceStatusRecorded(app);
  const moreInfoUrl = getCouncilTrackerUrl(app, proData);
  const isTracked = isTr ? isTr(app.id) : tracked;
  const canViewPro = canAccessProCouncil({ tier, proCouncils, council: app.council });
  const hasAnyProContent = hasProIntelligenceContent(proData, tier) || (proData?.trusted_parties?.length > 0);
  const showMapPreview = hasCoords && GMAP_KEY && !mapPreviewFailed;
  const proposalType = resolveProposalType(app);
  const proposalTypeEvidence = app.proposal_type_ai ? truncateEvidenceQuote(app.proposal_type_ai_evidence) : '';

  useEffect(() => {
    setMapPreviewFailed(false);
  }, [appId, app.latitude, app.longitude]);

  // Fetch Pro data whenever the DA changes.
  // Serves from in-memory cache immediately on revisit to avoid loading flash.
  // Uses a cancelled flag to guard stale writes when the user switches cards mid-flight.
  // proRetry increments trigger a re-fetch (bypasses cache via helper miss on error paths).
  useEffect(() => {
    if (!isPro || !canViewPro || subLoading || authLoading || !userId) {
      setProData(null);
      setProLoading(false);
      setProError(null);
      return;
    }

    if (!navigator.onLine) {
      setProLoading(false);
      setProError('You\'re offline — could not load Pro data.');
      reportClientDiagnostic('pro_fetch_offline', {
        severity: 'warning',
        user: diagnosticUser,
        surface: 'detail',
        page: 'da_detail',
        da: proDiagnosticDa,
        message: 'Phone was offline when Pro intelligence was requested',
        metadata: { tier },
      });
      return;
    }

    // Serve from cache immediately — skip loading state entirely
    const cacheKey = `${userId || 'user'}:${appId}`;
    const cached = proDataCache.get(cacheKey);
    if (cached) {
      setProData(cached);
      setProLoading(false);
      setProError(null);
      return;
    }

    let cancelled = false;
    setProData(null);
    setProError(null);
    setProLoading(true);

    const fetchProData = async () => {
      const startedAt = Date.now();
      let retryScheduled = false;
      try {
        const data = await fetchDAProDetail(appId, {
          label: 'DADetailView',
          cacheScope: userId,
          bypassCache: proRetry > 0,
        });
        if (cancelled) return;
        const elapsedMs = logSlowProFetch(appId, startedAt, {
          surface: 'detail',
          council: appCouncil,
          da_number: appDaNumber,
          source: appSource,
          is_primary: appIsPrimary,
          parent_da_id: appParentDaId,
          has_parent: Boolean(appParentDaId),
        });
        if (elapsedMs >= 4000) {
          reportClientDiagnostic('slow_pro_fetch', {
            severity: 'warning',
            user: diagnosticUser,
            surface: 'detail',
            page: 'da_detail',
            da: proDiagnosticDa,
            message: `Pro intelligence was slow (${elapsedMs}ms)`,
            timing: { elapsed_ms: elapsedMs },
            metadata: { tier },
          });
        }
        setProData(data);
        if (isProSnapshotMissing(data)) {
          reportClientDiagnostic('pro_snapshot_pending', {
            severity: 'info',
            user: diagnosticUser,
            surface: 'detail',
            page: 'da_detail',
            da: proDiagnosticDa,
            message: 'Pro Intelligence snapshot is queued for preparation',
            metadata: {
              tier,
              snapshot_source: data?.snapshot_source,
              pro_snapshot_missing: true,
            },
          });
        } else if (!hasProIntelligenceContent(data, tier) && !(data?.trusted_parties?.length > 0)) {
          logProIntelligenceGap(appId, 'empty_pro_payload', {
            surface: 'detail',
            council: appCouncil,
            da_number: appDaNumber,
          });
          reportClientDiagnostic('empty_pro_payload', {
            severity: 'warning',
            user: diagnosticUser,
            surface: 'detail',
            page: 'da_detail',
            da: proDiagnosticDa,
            message: 'Pro intelligence returned no usable content',
            metadata: {
              tier,
              trusted_count: data?.trusted_parties?.length || 0,
              has_supply_chain: Array.isArray(data?.supply_chain) && data.supply_chain.length > 0,
              snapshot_source: data?.snapshot_source,
            },
          });
        }
      } catch (err) {
        if (cancelled) return;
        console.error('[DADetailView] Pro data fetch failed:', err.message);
        logProIntelligenceGap(appId, 'pro_fetch_failed', {
          surface: 'detail',
          council: appCouncil,
          da_number: appDaNumber,
          message: err.message,
        });
        reportClientDiagnostic('pro_fetch_failed', {
          severity: 'error',
          user: diagnosticUser,
          surface: 'detail',
          page: 'da_detail',
          da: proDiagnosticDa,
          message: err.message || 'Pro intelligence failed to load',
          metadata: { retry: proRetry, tier },
          error: err,
        });
        const willRetry = proRetry < PRO_AUTO_RETRY_LIMIT && navigator.onLine;
        if (willRetry) {
          retryScheduled = true;
          setProLoading(false);
          setProError(PRO_RETRYING_MESSAGE);
          setTimeout(() => {
            if (!cancelled) {
              setProRetryState(state => ({
                scope: proRetryScope,
                count: state.scope === proRetryScope ? state.count + 1 : 1,
              }));
            }
          }, proRetryDelayMs(err));
        } else {
          setProError(isSupabaseCircuitError(err)
            ? 'Supabase is cooling down. Try again in a moment.'
            : `Could not load Pro data${err?.message ? `: ${err.message}` : ''}`);
        }
      } finally {
        if (!cancelled && !retryScheduled) setProLoading(false);
      }
    };

    fetchProData();

    return () => { cancelled = true; };
  }, [
    appId,
    appCouncil,
    appDaNumber,
    appSource,
    appIsPrimary,
    appParentDaId,
    diagnosticUser,
    proDiagnosticDa,
    proRetryScope,
    userId,
    isPro,
    canViewPro,
    subLoading,
    authLoading,
    proRetry,
    tier,
  ]);

  const handleShare = async (e) => {
    e.stopPropagation();
    const appNum = app.da_number;
    if (!appNum) return;
    const shareUrl = `https://sitescopeapp.com/da?app=${encodeURIComponent(appNum)}&council=${encodeURIComponent(app.council || '')}`;
    trackDAEvent('da_share', app, { method: navigator.share ? 'native_share' : 'clipboard', surface: 'detail' }, { page: 'da_detail', source: 'share_button' });

    if (user) {
      supabase.from('share_events').insert({
        event_type: 'share', da_id: app.id,
        council_application_number: appNum, council: app.council, shared_by: user.id,
      }).then(({ error }) => { if (error) console.error('[SiteScope] share_events insert failed:', error.message); });
    }

    setSharePulsed(true);
    setTimeout(() => setSharePulsed(false), 600);

    if (navigator.share) {
      navigator.share({ title: `DA ${appNum}`, text: `${displayDAAddress(app) || appNum} — ${app.status || ''}`, url: shareUrl }).catch(() => {});
    } else {
      try { await navigator.clipboard.writeText(shareUrl); showToast('Link copied to clipboard'); }
      catch { showToast('Copy this link: ' + shareUrl); }
    }
  };

  // Categorise for display
  const displayAddress = displayDAAddress(app);
  const addressLine1 = displayAddress ? displayAddress.split(',')[0] : isPlaceholderAddress(app.address) ? unavailableAddressLabel : displayIdentifier;
  const addressRest = displayAddress ? displayAddress.split(',').slice(1).join(',').trim() : addressLocality(app);
  const council = app.council || '';

  return (
    <div ref={detailRef} role="region" aria-label="Application detail" onScroll={event => onScrollPositionChange?.(event.currentTarget.scrollTop)} style={{
      position: "fixed", inset: 0, background: COLORS.bg,
      zIndex: 200, overflowY: "auto",
      fontFamily: "'DM Sans', sans-serif",
    }}>
      <div style={{
        width: "100%",
        maxWidth: isDesktop ? "none" : 430,
        boxSizing: "border-box",
        margin: "0 auto",
        padding: isDesktop ? "0 24px 34px" : "0 16px 80px",
      }}>

        {/* Back nav */}
        <div style={{
          display: "flex", alignItems: "center", gap: 8,
          padding: isDesktop ? "14px 0" : "12px 0 8px",
          position: "sticky", top: 0, background: COLORS.bg, zIndex: 10,
          borderBottom: isDesktop ? `1px solid ${COLORS.cardBorder}` : "none",
          marginBottom: isDesktop ? 20 : 0,
        }}>
          <button
            onClick={onBack}
            style={{ background: "none", border: "none", padding: 0, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={COLORS.textMuted} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6"/>
            </svg>
            <span style={{ color: COLORS.textMuted, fontSize: 14, fontWeight: 600 }}>Back</span>
          </button>
          {isDesktop && (
            <div style={{ color: COLORS.textMuted, fontSize: 12, fontWeight: 650 }}>
              <span style={{ opacity: 0.75 }}>Dashboard</span>
              <span style={{ margin: "0 8px", opacity: 0.55 }}>›</span>
              <span style={{ color: COLORS.text }}>{[displayIdentifier, addressLine1].filter(Boolean).join(' — ')}</span>
            </div>
          )}
          <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 8, justifyContent: "flex-end" }}>
            <AppTypeBadge type={app.application_type} />
            {canViewPro && <ProBadge />}
          </div>
        </div>

        <div style={isDesktop ? {
          display: "grid",
          gridTemplateColumns: "minmax(360px, 460px) minmax(0, 1fr)",
          gap: 24,
          alignItems: "start",
        } : {}}>
          <div style={isDesktop ? { position: "sticky", top: 73, alignSelf: "start" } : {}}>

        {/* Title + status */}
        <div style={{ marginBottom: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
            <h1 style={{ color: COLORS.text, fontSize: 24, fontWeight: 800, margin: 0, lineHeight: 1.2, flex: 1 }}>
              {addressLine1}
            </h1>
            <StatusBadge status={displayStatus} />
          </div>
          {(addressRest || council) && (
            <div style={{ color: COLORS.textMuted, fontSize: 13, marginTop: 4 }}>
              {[addressRest, council].filter(Boolean).join(' · ')}
            </div>
          )}
          {displayIdentifier && (displayAddress || isPlaceholderAddress(app.address)) && (
            <div style={{ color: COLORS.textDim, fontSize: 12, marginTop: 4, fontFamily: "'JetBrains Mono',monospace" }}>
              {displayIdentifier}
            </div>
          )}
        </div>

        {/* Description */}
        {app.description && (
          <div style={{
            background: COLORS.card, border: `1px solid ${COLORS.cardBorder}`,
            borderRadius: 10, padding: 14, marginBottom: 16,
          }}>
            <div style={{ color: COLORS.textDim, fontSize: 10, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>{/modification/i.test(app.application_type || "") ? "Underlying project / recorded scope" : "Proposed work"}</div>
            <p style={{ color: COLORS.textMuted, fontSize: 14, lineHeight: 1.6, margin: 0 }}>{app.description}</p>
            <DALifecycleSection daId={app.id} theme={theme} />
          </div>
        )}
        {/modification/i.test(app.application_type || "") && <p style={{ color: COLORS.textMuted, fontSize: 13 }}>This is a modification of an existing application. The recorded scope may describe the underlying project; check the council record for the exact amendment before treating it as new work.</p>}
        {!app.description && <DALifecycleSection daId={app.id} theme={theme} />}

        {proposalTypeEvidence && (
          <div style={{
            background: COLORS.card,
            border: `1px solid ${COLORS.cardBorder}`,
            borderRadius: 10,
            padding: 12,
            marginBottom: 16,
            color: COLORS.textMuted,
            fontSize: 12,
            lineHeight: 1.45,
          }}>
            <span style={{ color: COLORS.amber, fontWeight: 800 }}>
              Categorised as {getCategoryLabel(proposalType)}
            </span>
            <span> - "{proposalTypeEvidence}"</span>
          </div>
        )}

        <p style={{ color: COLORS.textMuted, fontSize: 13, lineHeight: 1.5 }}>Planning status is not construction progress. Builder appointment and work availability need confirmation.</p>
        <p style={{ color: COLORS.textMuted, fontSize: 13 }}>Application: {app.application_type || 'Type not supplied'}</p>
        {/* Action buttons */}
        <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
          {onTrack && (
            <button
              onClick={() => onTrack(app)}
              style={{
                flex: 2,
                background: isTracked
                  ? `${COLORS.green}22`
                  : `linear-gradient(135deg, ${COLORS.amber}, ${COLORS.amberDark})`,
                border: isTracked ? `1px solid ${COLORS.green}44` : "none",
                color: isTracked ? COLORS.green : COLORS.bg,
                borderRadius: 8, padding: "12px 16px",
                fontSize: 15, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
              }}
            >
              {isTracked ? '✓ Tracking this DA' : '★ Track this DA'}
            </button>
          )}
          <button
            onClick={handleShare}
            style={{
              flex: 1, background: COLORS.card, border: `1px solid ${COLORS.cardBorder}`,
              color: COLORS.text, borderRadius: 8, padding: "12px 16px",
              fontSize: 15, fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
            }}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/>
            </svg>
            Share
          </button>
          {moreInfoUrl && (
            <a
              href={moreInfoUrl} target="_blank" rel="noopener noreferrer"
              onClick={() => trackDAEvent('da_council_click', app, { url: moreInfoUrl, surface: 'detail' }, { page: 'da_detail', source: 'council_link' })}
              style={{
                flex: 1, background: COLORS.card, border: `1px solid ${COLORS.cardBorder}`,
                color: COLORS.textMuted, borderRadius: 8, padding: "12px 16px",
                fontSize: 13, fontWeight: 600, textDecoration: "none",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}
            >{councilLinkLabel(moreInfoUrl, app, proData)}</a>
          )}
        </div>

        {/* Key stats row */}
        <CouncilSearchHelp app={app} proData={proData} url={moreInfoUrl} color={COLORS.textMuted} accent={COLORS.amber} />
        <DATimeline app={app} theme={theme} />

        {(cost || lodgedDate || daysInStatus !== null) && (
          <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
            <StatTile label="Est. Cost" value={cost} color={COLORS.amber} />
            <StatTile label="Lodged" value={lodgedDate} />
            {daysInStatus !== null && <StatTile label="Days since status recorded" value={`${daysInStatus}d`} />}
          </div>
        )}

        {/* Map preview */}
        {showMapPreview && (() => {
          const params = new URLSearchParams({
            center: `${lat},${lng}`,
            zoom: '16',
            size: '600x200',
            scale: '2',
            maptype: 'hybrid',
            markers: `color:orange|${lat},${lng}`,
            key: GMAP_KEY,
          });
          params.append('style', 'feature:poi|visibility:off');
          params.append('style', 'feature:transit|visibility:off');
          const src = `https://maps.googleapis.com/maps/api/staticmap?${params.toString()}`;
          return (
          <div
            onClick={() => onShowOnMap && onShowOnMap(app)}
            style={{
              borderRadius: 10, height: 132, marginBottom: 16,
              position: "relative", overflow: "hidden",
              border: `1px solid ${COLORS.cardBorder}`,
              background: "#1e4060",
              cursor: "pointer",
            }}
          >
            <img
              src={src}
              alt="DA location"
              referrerPolicy="no-referrer"
              onError={() => setMapPreviewFailed(true)}
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
            <div style={{
              position: "absolute", top: 10, right: 10,
              background: COLORS.card + "dd", borderRadius: 6, padding: "4px 10px",
            }}>
              <span style={{ color: COLORS.amber, fontSize: 10, fontWeight: 600 }}>Tap to open map →</span>
            </div>
          </div>
          );
        })()}
        {!hasCoords && (
          <div style={{
            borderRadius: 10, marginBottom: 16, padding: "14px 16px",
            border: `1px solid ${COLORS.cardBorder}`,
            background: COLORS.card,
            color: COLORS.textDim,
            fontSize: 13,
            lineHeight: 1.45,
          }}>
            Map unavailable until this DA has verified coordinates.
          </div>
        )}

        {/* Key details grid */}
        {(dwellings > 0 || storeys > 0 || app.lot_id_string) && (
          <div style={{
            display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12,
            background: COLORS.card, border: `1px solid ${COLORS.cardBorder}`,
            borderRadius: 10, padding: 14, marginBottom: 16,
          }}>
            {dwellings > 0 && (
              <div>
                <div style={{ color: COLORS.textDim, fontSize: 10, textTransform: "uppercase", letterSpacing: 0.5 }}>Dwellings</div>
                <div style={{ color: COLORS.text, fontSize: 14, fontWeight: 600 }}>{dwellings}</div>
              </div>
            )}
            {storeys > 0 && (
              <div>
                <div style={{ color: COLORS.textDim, fontSize: 10, textTransform: "uppercase", letterSpacing: 0.5 }}>Storeys</div>
                <div style={{ color: COLORS.text, fontSize: 14, fontWeight: 600 }}>{storeys}</div>
              </div>
            )}
            {app.lot_id_string && (
              <div>
                <div style={{ color: COLORS.textDim, fontSize: 10, textTransform: "uppercase", letterSpacing: 0.5 }}>Lot / Plan</div>
                <div style={{ color: COLORS.text, fontSize: 12, fontFamily: "'JetBrains Mono',monospace" }}>{app.lot_id_string}</div>
              </div>
            )}
          </div>
        )}

        {/* Private note */}
        {user && (
          <div style={{ marginBottom: 20 }} onClick={e => e.stopPropagation()}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
              <span style={{ color: COLORS.textMuted, fontSize: 11, textTransform: "uppercase", letterSpacing: 0.8, fontWeight: 600 }}>Private Note</span>
              <span style={{ fontSize: 11, color: COLORS.green, opacity: note.saved ? 1 : 0, transition: "opacity 0.4s" }}>{note.saved ? 'Saved' : ''}</span>
            </div>
            {note.error && <div role="alert" style={{ color: COLORS.orange, marginBottom: 8 }}>{note.error}{note.loaded && <button onClick={note.handleBlur}>Retry save</button>}</div>}
            <textarea
              value={note.text}
              onChange={e => note.handleChange(e.target.value)}
              aria-label="Private note and next step"
              disabled={!note.loaded}
              placeholder="Next step, who to check with, and when…"
              rows={3}
              style={{
                width: "100%", boxSizing: "border-box",
                background: COLORS.card, border: `1px solid ${COLORS.cardBorder}`,
                borderRadius: 8, color: COLORS.text,
                fontSize: "var(--text-input, 16px)", fontFamily: "'DM Sans', sans-serif",
                lineHeight: 1.6, padding: "10px 12px",
                resize: "vertical", outline: "none",
                transition: "border-color 0.15s",
              }}
              onFocus={e => e.target.style.borderColor = COLORS.amber + "66"}
              onBlur={e => { e.target.style.borderColor = COLORS.cardBorder; note.handleBlur(); }}
            />
          </div>
        )}
          </div>

          <div style={isDesktop ? {
            minWidth: 0,
            paddingLeft: 22,
            borderLeft: `1px solid ${COLORS.cardBorder}`,
          } : {}}>

        {/* Pro Intelligence — desktop keeps a visible right-side panel; phone keeps the quiet inline behaviour. */}
        {isDesktop && isPro && (
          <div style={{
            background: COLORS.card,
            border: `1px solid ${COLORS.cardBorder}`,
            borderRadius: 10,
            padding: 18,
            minHeight: 220,
          }}>
            <div style={{
              display: "flex", alignItems: "center", gap: 8, marginBottom: 16,
              paddingBottom: 12, borderBottom: `1px solid ${COLORS.cardBorder}`,
            }}>
              <ProBadge />
              <span style={{ color: COLORS.amber, fontSize: 12, fontWeight: 800, letterSpacing: 0.5, textTransform: "uppercase" }}>Pro Intelligence</span>
            </div>

            {(subLoading || authLoading) && (
              <div style={{ color: COLORS.textDim, fontSize: 13, lineHeight: 1.5 }}>
                Checking Pro access…
              </div>
            )}

            {!subLoading && !authLoading && !canViewPro && (
              <div style={{ color: COLORS.textDim, fontSize: 13, lineHeight: 1.5 }}>
                Pro Intelligence is available for councils included in your Pro plan.
              </div>
            )}

            {!subLoading && !authLoading && canViewPro && proLoading && (
              <>
                <style>{`@keyframes proShimmer { 0%,100% { opacity: 0.18; } 50% { opacity: 0.48; } }`}</style>
                <div style={{ height: 3, borderRadius: 3, background: COLORS.amber, animation: 'proShimmer 1.8s ease-in-out infinite', marginBottom: 12 }} />
                <div style={{ color: COLORS.textDim, fontSize: 13, lineHeight: 1.5 }}>
                  Loading Pro Intelligence…
                </div>
              </>
            )}

            {!subLoading && !authLoading && canViewPro && !proLoading && proError && (
              <div style={{ padding: '4px 0' }}>
                <div style={{ color: COLORS.textDim, fontSize: 13, marginBottom: 10, lineHeight: 1.5 }}>{proError}</div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button type="button" onClick={() => setProRetryState(state => ({ scope: proRetryScope, count: state.scope === proRetryScope ? state.count + 1 : 1 }))} style={{ background: COLORS.amber, color: '#081525', border: 'none', borderRadius: 999, padding: '7px 12px', fontSize: 12, fontWeight: 800, cursor: 'pointer' }}>Retry</button>
                  <button type="button" onClick={refreshAppShell} style={{ background: 'transparent', color: COLORS.textDim, border: `1px solid ${COLORS.cardBorder}`, borderRadius: 999, padding: '7px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Repair app</button>
                </div>
              </div>
            )}

            {!subLoading && !authLoading && canViewPro && !proLoading && !proError && proData && (
              <ProIntelligence appRecord={app} proData={proData} theme={theme} tier={tier} trustedParties={proData?.trusted_parties || []} showEmpty />
            )}

            {!subLoading && !authLoading && canViewPro && !proLoading && !proError && !proData && (
              <div style={{ color: COLORS.textDim, fontSize: 13, lineHeight: 1.5 }}>
                Pro Intelligence is preparing for this DA.
              </div>
            )}
          </div>
        )}

        {!isDesktop && isPro && proLoading && (
          <style>{`@keyframes proShimmer { 0%,100% { opacity: 0.15; } 50% { opacity: 0.4; } }`}</style>
        )}
        {!isDesktop && isPro && proLoading && (
          <div style={{
            height: 2, borderRadius: 2, marginTop: 20,
            background: COLORS.amber,
            animation: 'proShimmer 1.8s ease-in-out infinite',
          }} />
        )}

        {!isDesktop && isPro && !proLoading && proError && (
          <div style={{ padding: '10px 0', textAlign: 'center' }}>
            <div style={{ color: COLORS.textDim, fontSize: 12, marginBottom: 8 }}>{proError}</div>
            <div style={{ display: 'flex', justifyContent: 'center', gap: 8, flexWrap: 'wrap' }}>
              <button type="button" onClick={() => setProRetryState(state => ({ scope: proRetryScope, count: state.scope === proRetryScope ? state.count + 1 : 1 }))} style={{ background: COLORS.amber, color: '#081525', border: 'none', borderRadius: 999, padding: '7px 12px', fontSize: 12, fontWeight: 800, cursor: 'pointer' }}>Retry</button>
              <button type="button" onClick={refreshAppShell} style={{ background: 'transparent', color: COLORS.textDim, border: `1px solid ${COLORS.cardBorder}`, borderRadius: 999, padding: '7px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Repair app</button>
            </div>
          </div>
        )}

        {!isDesktop && isPro && !proLoading && !proError && (hasAnyProContent || proData) && (
          <>
            <style>{`@keyframes proFadeIn { from { opacity: 0; transform: translateY(5px); } to { opacity: 1; transform: translateY(0); } }`}</style>
            <div style={{ animation: 'proFadeIn 0.3s ease' }}>
              <div style={{
                display: "flex", alignItems: "center", gap: 8, marginBottom: 16,
                padding: "10px 0", borderBottom: `1px solid ${COLORS.cardBorder}`,
              }}>
                <ProBadge />
                <span style={{ color: COLORS.amber, fontSize: 12, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase" }}>Pro Intelligence</span>
              </div>
              <ProIntelligence appRecord={app} proData={proData} theme={theme} tier={tier} trustedParties={proData?.trusted_parties || []} showEmpty />
            </div>
          </>
        )}
          {!isPro && isDesktop && (
            <div style={{
              background: COLORS.card,
              border: `1px solid ${COLORS.cardBorder}`,
              borderRadius: 10,
              padding: 18,
              color: COLORS.textMuted,
              fontSize: 13,
              lineHeight: 1.5,
            }}>
              Pro Intelligence appears here for paid Pro accounts.
            </div>
          )}
          </div>
        </div>
      </div>
    </div>
  );
}
